import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/* eslint-disable no-restricted-globals */
import { useEffect, useMemo } from "react";
import { Box, useTokens } from "@gemini/core";
import { ArrowLeftIcon, ArrowRightIcon, MagnifyingGlassIcon, } from "@gemini/icons";
import { InfoEmailSentSpot } from "@gemini/illustrations";
import { Button, TextButton } from "@gemini/ui";
import { useClassifiedId, useHardFacts, useTracking, } from "@/contexts/classified";
import { useContactCardSection, useContactSections } from "@/contexts/contact";
import { useConfirmationPage } from "@/contexts/confirmPage";
import { useBackToSearch, useTrackingView } from "@/hooks";
import { useI18n } from "@/shared/hooks";
import { saveContactToStorage } from "@/utils/saveContactAndSeenToStorage/saveContactAndSeenToStorage";
import { confirmPageTestId } from "../contacting/constants";
import { LoginNudge } from "../LoginNudge";
import { ConfirmPageSimilarListings } from "./ConfirmPageSimilarListings";
import { useBrowserAPI } from "@/hooks/useBrowserAPI";
import { useGetBaseRoute } from "@/app/router";
import { useAuthentication } from "@aviv-unified-frontend/runtime/authentication";
export const ConfirmPage = () => {
    var _a;
    const { t } = useI18n();
    const tokens = useTokens();
    const { source, partnerTitles } = useConfirmationPage();
    const { state } = useAuthentication();
    // @ts-ignore
    const { title: agencyTitle } = useContactCardSection();
    // @ts-ignore
    const { formatted: formattedPrice } = ((_a = useHardFacts()) === null || _a === void 0 ? void 0 : _a.price) || {};
    // @ts-ignore
    const { av_zip_code, av_city } = useTracking();
    const classifiedId = useClassifiedId();
    const baseRoute = useGetBaseRoute();
    const isFinanceOfferEnabled = 
    // @ts-ignore
    useContactSections().confirmationPage.isFinanceOfferButtonEnabled;
    const isUserLoggedIn = state === "authenticated";
    const headerHeight = tokens["sizing.64"]; // see https://github.com/axel-springer-kugawana/aviv_whitelabel_header_footer/blob/main/white-label/src/components/header/header.tsx
    const agencyTitles = useMemo(() => {
        var _a;
        const titles = [
            ...new Set([agencyTitle, ...(partnerTitles !== null && partnerTitles !== void 0 ? partnerTitles : [])]
                .filter(Boolean)
                .map((s) => s.trim())),
        ];
        return titles.length <= 1
            ? (_a = titles[0]) !== null && _a !== void 0 ? _a : ""
            : `${titles.slice(0, -1).join(", ")} ${t("confirm_page.conjunction_and")} ${titles[titles.length - 1]}`;
    }, [agencyTitle, partnerTitles, t]);
    const { bWindow } = useBrowserAPI();
    useEffect(() => {
        // @ts-ignore
        saveContactToStorage(classifiedId);
    }, [classifiedId]);
    useEffect(() => {
        bWindow.scrollTo(0, 0);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const handleBackButton = () => {
        bWindow.location.href = baseRoute();
    };
    const handleFinanceOfferButtonPress = () => {
        const url = new URL("/immobilienfinanzierung-anfragen/", bWindow.location.origin);
        // @ts-ignore
        url.searchParams.set("classifiedId", classifiedId);
        // @ts-ignore
        url.searchParams.set("price", formattedPrice);
        url.searchParams.set("zip", av_zip_code);
        url.searchParams.set("city", av_city);
        url.searchParams.set("originvariant", "WL_CONTACT");
        bWindow.location.href = url.href;
    };
    const { handleBackToSearch } = useBackToSearch();
    useTrackingView();
    const agencyText = source === "form"
        ? t("confirm_page.body", {
            replace: { agencyTitle },
        })
        : t("confirm_page.body_plural", {
            replace: { agencyTitles },
        });
    return (_jsxs(Box, { testId: confirmPageTestId, minHeight: `calc(100vh - ${headerHeight} - 500px)`, maxWidth: "1280px", width: "100%", paddingHorizontal: {
            "breakpoint.base": "spacing.none",
            "breakpoint.lg": "spacing.80",
        }, paddingBottom: {
            "breakpoint.base": "spacing.none",
            "breakpoint.lg": "spacing.24",
        }, marginHorizontal: "auto", display: "flex", flexDirection: "column", gap: "spacing.32", children: [_jsxs(Box, { width: "100%", paddingTop: "spacing.24", paddingHorizontal: {
                    "breakpoint.base": "spacing.16",
                    "breakpoint.lg": "spacing.none",
                }, display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", gap: "spacing.24", children: [_jsx(Box, { width: "100%", children: _jsx(TextButton, { icon: ArrowLeftIcon, iconPosition: "start", onPress: handleBackButton, children: t("confirm_page.back_to_listing.cta.label") }) }), _jsxs(Box, { maxWidth: {
                            "breakpoint.base": "280px",
                            "breakpoint.lg": "738px",
                        }, display: "flex", flexDirection: {
                            "breakpoint.base": "column",
                            "breakpoint.lg": "row",
                        }, alignItems: {
                            "breakpoint.base": "center",
                            "breakpoint.lg": "flex-start",
                        }, gap: {
                            "breakpoint.base": "spacing.12",
                            "breakpoint.lg": "spacing.48",
                        }, children: [_jsx(Box, { children: _jsx(InfoEmailSentSpot, { size: "96", "aria-label": "Confirm email info" }) }), _jsxs(Box, { display: "flex", flexDirection: "column", alignItems: {
                                    "breakpoint.base": "center",
                                    "breakpoint.lg": "start",
                                }, gap: "spacing.8", children: [_jsx(Box, { typography: {
                                            "breakpoint.base": "typography.headline.24.bold",
                                            "breakpoint.lg": "typography.headline.32.bold",
                                        }, textAlign: {
                                            "breakpoint.base": "center",
                                            "breakpoint.lg": "left",
                                        }, children: source === "form"
                                            ? t("confirm_page.headline")
                                            : t("confirm_page.headline_plural") }), _jsx(Box, { typography: {
                                            "breakpoint.base": "typography.body.16.regular",
                                            "breakpoint.lg": "typography.headline.20.regular",
                                        }, textAlign: {
                                            "breakpoint.base": "center",
                                            "breakpoint.lg": "left",
                                        }, dangerouslySetInnerHTML: { __html: agencyText } }), _jsxs(Box, { maxWidth: {
                                            "breakpoint.base": "343px",
                                            "breakpoint.lg": "738px",
                                        }, minWidth: {
                                            "breakpoint.base": "343px",
                                            "breakpoint.lg": "unset",
                                        }, display: "flex", flexDirection: {
                                            "breakpoint.base": "column",
                                            "breakpoint.lg": "row",
                                        }, alignItems: "center", justifyContent: "center", marginTop: "spacing.16", gap: "spacing.12", children: [_jsx(Button, { icon: MagnifyingGlassIcon, iconPosition: "start", onPress: handleBackToSearch, isFullWidth: {
                                                    "breakpoint.base": true,
                                                    "breakpoint.lg": false,
                                                }, children: t("confirm_page.back_to_search.cta.label") }), isFinanceOfferEnabled && (_jsx(Button, { variant: "secondary", icon: ArrowRightIcon, iconPosition: "end", onPress: handleFinanceOfferButtonPress, isFullWidth: {
                                                    "breakpoint.base": true,
                                                    "breakpoint.lg": false,
                                                }, children: t("classified_card.partner_links.financing.empty_price") }))] })] })] })] }), !isUserLoggedIn && _jsx(LoginNudge, {}), _jsx(ConfirmPageSimilarListings, {})] }));
};
